import { createContext, useContext, useMemo, useState } from "react";
import { Rfx } from "../types/rfx.types";
import { isRfxOnboardingType } from "../utils";

interface Uploads {
	rfxProcurer: any[]; // you can replace `any` with your file type
	rfxBidder: any[];
}

interface RfxContextType {
	currentCompanyId: number | null;
	guestUserId: number | null;
	isOnboardingRFx: boolean;
	rfxData: any; // replace `any` with your RFQ data type
	rfqId: number | null;
	saveHandler: (() => void) | null;
	uploadInProgresses: Uploads;

	// Functions / setters
	setCurrentCompanyId: (id: number | null) => void;
	setGuestUserId: (id: number | null) => void;
	setRFxData: (data: any) => void;
	setRFQId: (id: number | null) => void;
	setSaveHandler: (handler: (() => void) | null) => void;
	setUploadInProgresses: (uploads: Uploads) => void;
}

export const RfxContext = createContext<RfxContextType>(null);

export const RfxContextProvider = ({ children }) => {
	const [rfqId, setRFQId] = useState<number | null>(null);
	const [rfxData, setRFxData] = useState<Rfx | null>();
	const [guestUserId, setGuestUserId] = useState<number | null>(null);
	const [currentCompanyId, setCurrentCompanyId] = useState<number | null>(null);
	const [uploadInProgresses, setUploadInProgresses] = useState<Uploads | null>({
		rfxProcurer: [],
		rfxBidder: [],
	});
	const [saveHandler, setSaveHandler] = useState(null);

	const { isOnboardingRFx } = useMemo(() => {
		const isOnboardingRFx = isRfxOnboardingType(
			rfxData?.rfxType || rfxData?.type
		);

		return { isOnboardingRFx: isOnboardingRFx };
	}, [rfxData?.rfxType, rfxData?.type]);

	const onSettingRfxData = (rfxData: Rfx | null): void => {
		setRFxData(rfxData);
		setRFQId(rfxData?.id ?? rfqId);
	};

	return (
		<RfxContext.Provider
			value={{
				currentCompanyId,
				guestUserId,
				isOnboardingRFx: isOnboardingRFx ?? false,
				rfxData,
				rfqId,
				saveHandler,
				uploadInProgresses,

				// Functions / setters
				setCurrentCompanyId,
				setGuestUserId,
				setRFxData: onSettingRfxData,
				setRFQId,
				setSaveHandler,
				setUploadInProgresses,
			}}
		>
			{children}
		</RfxContext.Provider>
	);
};

export const useRfxContext = (): RfxContextType => {
	const context = useContext(RfxContext);
	if (!context) {
		throw new Error("useRfxContext must be used within RfxContextProvider");
	}
	return context;
};
